import { ComponentProps } from "react";
import { useInView } from "react-intersection-observer";
import Disclaimer from "@features/taboola/components/Disclaimer/Disclaimer";
import Text from "@features/taboola/components/Disclaimer/Text";
import useSendDataToGTM from "@src/features/analytics/hooks/useSendDataToGTM";
import { IS_PLAYWRIGHT } from "@util/constant";
import { cn } from "@util/helpers";

type DeeperdiveProps = {
  /** The URL path of the page. */
  urlPath: string;
  /** The counter of the Deeperdive component. */
  counter?: number;
  /** The className for disclaimer component. */
  disclaimerClassName?: string;
  /** The GA variant for the Deeperdive component. */
  gaVariant?: string;
} & Pick<ComponentProps<typeof Text>, "isArticlePage">;

/**
 * The Deeperdive component.
 * @figma https://www.figma.com/design/9RrH1KYIUOZk6rvgzlBBKX/WEB-BAU-2026?node-id=0-1&p=f&m=dev
 * */
const Deeperdive = ({
  urlPath,
  counter,
  isArticlePage = false,
  disclaimerClassName,
  gaVariant,
}: DeeperdiveProps): React.ReactElement => {
  const sendDataToGTM = useSendDataToGTM();
  const _counter = typeof counter === "number" ? `-${counter}` : "";

  const { ref: deeperdiveRef } = useInView({
    threshold: 0,
    onChange: (inView) => {
      if (!inView) return;
      if (!gaVariant) return;

      sendDataToGTM({
        event: "custom_event",
        eventCategory: "deeperdive",
        eventAction: "visible",
        eventLabel: window.location.href,
        abVariant: gaVariant,
        abVariant_user: gaVariant,
      });
    },
  });

  if (IS_PLAYWRIGHT) return <></>;

  return (
    <>
      <div
        ref={deeperdiveRef}
        data-testid="deeper-dive-component"
        id={`deeper-dive-container${_counter}`}
        className={cn("deeper-dive-container peer", {
          "md:px-8": isArticlePage,
        })}
        style={{ display: "none" }}
        aria-hidden="true"
        data-url={urlPath}
        data-section-name="deeperdive"
      ></div>

      <Disclaimer
        isArticlePage={isArticlePage}
        className={disclaimerClassName}
        gaVariant={gaVariant}
      />
    </>
  );
};

export default Deeperdive;
